import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Link,
  Toolbar,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getNavigationUrl, getTacticoreAppPath } from '../../utils/subdomainUtils';
import { navigateToUrl } from '../../utils/dashboardNavigation';
import HeaderAccountUtilities from '../layout/HeaderAccountUtilities';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';
import { isTacticoreLessonPlanAuthoringPath } from '../../../shared/tacticore/tacticoreAuthoringPaths';
import { useTacticoreAuthoringChromeMeta } from './TacticoreAuthoringChromeContext';

export interface TacticoreAuthoringChromeProps {
  /** Optional overrides; defaults come from authoring chrome context. */
  title?: string;
  organizationName?: string | null;
  saveStatus?: string | null;
}

const PRODUCT_NAV: Array<{ label: string; appPath: string; match: (path: string) => boolean }> = [
  {
    label: 'Lesson Plans',
    appPath: '/lesson-plans',
    match: (path) =>
      path === '/tacticore/lesson-plans' ||
      path === '/lesson-plans' ||
      path.startsWith('/tacticore/lesson-plans/') ||
      path.startsWith('/lesson-plans/'),
  },
  {
    label: 'Library',
    appPath: '/library',
    match: (path) => path === '/tacticore/library' || path === '/library',
  },
  {
    label: 'Exams',
    appPath: '/exams',
    match: (path) =>
      path === '/tacticore/exams' ||
      path === '/exams' ||
      path.startsWith('/tacticore/exams/') ||
      path.startsWith('/exams/'),
  },
];

/**
 * Compact TactiCore authoring header.
 * Replaces marketing Header on lesson-plan new/edit and manager lists when the UX flag is on.
 */
const TacticoreAuthoringChrome: React.FC<TacticoreAuthoringChromeProps> = ({
  title: titleProp,
  organizationName: orgProp,
  saveStatus: saveStatusProp,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const path = (pathname.replace(/\/$/, '') || '/').split('?')[0] ?? '/';
  const isForm = isTacticoreLessonPlanAuthoringPath(pathname);
  const plansPath = getTacticoreAppPath('/lesson-plans');
  const meta = useTacticoreAuthoringChromeMeta();
  const title = titleProp ?? meta.title;
  const organizationName = orgProp !== undefined ? orgProp : meta.organizationName;
  const saveStatus = saveStatusProp !== undefined ? saveStatusProp : meta.saveStatus;

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="default"
      data-testid="tacticore-authoring-chrome"
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        color: 'text.primary',
      }}
    >
      <Toolbar
        sx={{
          px: AGENCY_SHELL_HORIZONTAL_PX,
          minHeight: 56,
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {isForm ? (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(plansPath)}
            sx={{ flexShrink: 0 }}
          >
            Lesson Plans
          </Button>
        ) : (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigateToUrl(getNavigationUrl('/dashboard'), navigate)}
            data-testid="tacticore-apt-dashboard-link"
            sx={{ flexShrink: 0 }}
          >
            APT Dashboard
          </Button>
        )}
        <Typography
          variant="subtitle1"
          component="div"
          sx={{ fontWeight: 700, letterSpacing: 0.02, flexShrink: 0 }}
        >
          TactiCore
        </Typography>
        {!isForm ? (
          <Box
            component="nav"
            aria-label="TactiCore product"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', ml: 1 }}
          >
            {PRODUCT_NAV.map((item) => {
              const to = getTacticoreAppPath(item.appPath);
              const active = item.match(path);
              return (
                <Button
                  key={item.label}
                  size="small"
                  color={active ? 'primary' : 'inherit'}
                  variant={active ? 'text' : 'text'}
                  onClick={() => navigate(to)}
                  sx={{
                    fontWeight: active ? 700 : 500,
                    textDecoration: active ? 'underline' : 'none',
                    textUnderlineOffset: 3,
                    minWidth: 0,
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              ·
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" noWrap fontWeight={600}>
                {title}
              </Typography>
              {organizationName ? (
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {organizationName}
                </Typography>
              ) : null}
            </Box>
            {saveStatus ? (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                {saveStatus}
              </Typography>
            ) : null}
          </>
        )}
        {isForm ? null : <Box sx={{ flex: 1, minWidth: 0 }} />}
        <HeaderAccountUtilities />
      </Toolbar>
    </AppBar>
  );
};

/** Minimal footer for authoring immersive layouts (rendered by Layout / host shell). */
export const TacticoreAuthoringMiniFooter: React.FC = () => (
  <Box
    component="footer"
    data-testid="tacticore-authoring-mini-footer"
    sx={{
      py: 1,
      px: AGENCY_SHELL_HORIZONTAL_PX,
      borderTop: 1,
      borderColor: 'divider',
      display: 'flex',
      gap: 2,
      flexWrap: 'wrap',
      alignItems: 'center',
      typography: 'caption',
      color: 'text.secondary',
      bgcolor: 'background.paper',
    }}
  >
    <Typography variant="caption" fontWeight={600} color="text.primary">
      TactiCore
    </Typography>
    <Link
      href={getNavigationUrl('/help')}
      underline="hover"
      color="inherit"
      variant="caption"
    >
      Help
    </Link>
    <Link
      href={getNavigationUrl('/privacy')}
      underline="hover"
      color="inherit"
      variant="caption"
    >
      Privacy
    </Link>
  </Box>
);

export default TacticoreAuthoringChrome;
